import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';

import { Answer, RefusalCard } from '@/components/common/Answer';
import { AsOfStrip } from '@/components/common/AsOfStrip';
import { ExplainerCard } from '@/components/common/ExplainerCard';
import { FieldGuide } from '@/components/common/FieldGuide';
import { ErrorMessage, SkeletonLoader } from '@/components/common/StateViews';
import {
  Figure,
  PersonScopeCard,
  TD,
  TD_MUTED,
  TD_NUM,
  TH,
  TH_NUM,
  THEAD,
  caseHref,
  useMoney,
} from '@/pages/cvm/shared';
import { fetchCvmParty } from '@/services/cvm';
import type { CvmParty, CvmPartyRecordShape } from '@/services/types';

/**
 * One party RECORD, never a profile.
 *
 * What the documents say under this name, in date order: the judgments that
 * name it, each sanction row as recorded with its clause, the settlement
 * decisions, the CRSFN acórdãos, the register's status events. No fine is
 * totalled and nothing is compared with another party: the name is what CVM
 * printed, a second spelling is a second record, and most names here are
 * natural persons. The record refuses at RECORD scope when no document names
 * the party beyond a register event.
 */
export function CvmPartyPage() {
  const { slug = '' } = useParams();
  const { t } = useTranslation(['cvm', 'common']);
  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['cvm', 'party', slug],
    queryFn: () => fetchCvmParty(slug),
    enabled: slug.length > 0,
  });

  if (isPending) return <SkeletonLoader rows={8} />;
  if (error) return <ErrorMessage message={error.message} onRetry={() => void refetch()} />;

  return (
    <div className="space-y-4 p-6">
      <header className="space-y-1">
        <p className="text-xs text-muted-foreground">
          <Link to="/cvm/parties" className="underline-offset-2 hover:underline">
            {t('cvm:party.back')}
          </Link>
        </p>
        <h1 className="text-lg font-semibold text-foreground">{data.name}</h1>
        <p className="text-sm text-muted-foreground">
          {t(`cvm:entityTypes.${data.entity_type}`, {
            defaultValue: data.entity_type,
          })}
          {data.entity_type_rule
            ? ` · ${t('cvm:party.rule', { rule: data.entity_type_rule })}`
            : ''}
        </p>
      </header>

      <ExplainerCard i18nKey="cvm:party.explainer" />
      <AsOfStrip asOf={data.as_of} />
      <PersonScopeCard answer={data.person_scope} />

      <section className="rounded-lg border bg-card p-4" data-record>
        <h2 className="mb-2 text-sm font-semibold text-foreground">
          {t('cvm:party.record.title')}
        </h2>
        <Answer answer={data.record} help={t('cvm:party.record.help')} scope="record">
          {(r) => <RecordFigures record={r} />}
        </Answer>
      </section>

      <RefusalCard title={t('cvm:party.totals.title')} answer={data.party_totals} />

      <Sections data={data} />

      <FieldGuide i18nKey="cvm:party.guide" />
    </div>
  );
}

function RecordFigures({ record }: { record: CvmPartyRecordShape }) {
  const { t, i18n } = useTranslation(['cvm']);
  const number = new Intl.NumberFormat(i18n.language);
  return (
    <dl className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
      <Figure label={t('cvm:party.record.judgments')} value={number.format(record.judgments)} />
      <Figure
        label={t('cvm:party.record.sanctionRows')}
        value={number.format(record.sanction_rows)}
      />
      <Figure label={t('cvm:party.record.settlements')} value={number.format(record.settlements)} />
      <Figure label={t('cvm:party.record.appeals')} value={number.format(record.appeals)} />
      <Figure
        label={t('cvm:party.record.registerEvents')}
        value={number.format(record.register_events)}
      />
      <Figure
        label={t('cvm:party.record.span')}
        value={
          record.first_date
            ? record.last_date && record.last_date !== record.first_date
              ? `${record.first_date} – ${record.last_date}`
              : record.first_date
            : '—'
        }
      />
    </dl>
  );
}

function Sections({ data }: { data: CvmParty }) {
  const { t } = useTranslation(['cvm']);
  const money = useMoney();
  return (
    <div className="space-y-4">
      <section className="rounded-lg border bg-card p-4" data-judgments>
        <h2 className="mb-2 text-sm font-semibold text-foreground">
          {t('cvm:party.judgments.title')}
        </h2>
        {data.judgments.length === 0 ? (
          <p className="text-xs text-muted-foreground">{t('cvm:party.none')}</p>
        ) : (
          <table className="w-full text-sm">
            <thead className={THEAD}>
              <tr>
                <th className={TH}>{t('cvm:party.judgments.process')}</th>
                <th className={TH}>{t('cvm:party.judgments.date')}</th>
                <th className={TH}>{t('cvm:party.judgments.relator')}</th>
                <th className={TH}>{t('cvm:party.judgments.ementa')}</th>
                <th className={TH_NUM}>{t('cvm:party.judgments.rows')}</th>
              </tr>
            </thead>
            <tbody>
              {data.judgments.map((j) => (
                <tr key={j.case_key} className="border-t align-top">
                  <td className={TD}>
                    <Link
                      to={caseHref(j.case_key)}
                      className="text-primary underline-offset-2 hover:underline"
                    >
                      {j.process_number}
                    </Link>
                  </td>
                  <td className={TD_MUTED}>{j.judgment_date ?? '—'}</td>
                  <td className={TD_MUTED}>{j.relator ?? '—'}</td>
                  <td className={TD_MUTED}>{j.ementa ?? '—'}</td>
                  <td className={TD_NUM}>{j.sanction_rows}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section className="rounded-lg border bg-card p-4" data-sanctions>
        <h2 className="mb-2 text-sm font-semibold text-foreground">
          {t('cvm:party.sanctions.title')}
        </h2>
        {data.sanctions.length === 0 ? (
          <p className="text-xs text-muted-foreground">{t('cvm:party.none')}</p>
        ) : (
          <table className="w-full text-sm">
            <thead className={THEAD}>
              <tr>
                <th className={TH}>{t('cvm:party.sanctions.process')}</th>
                <th className={TH}>{t('cvm:party.sanctions.type')}</th>
                <th className={TH_NUM}>{t('cvm:party.sanctions.amount')}</th>
                <th className={TH}>{t('cvm:party.sanctions.provision')}</th>
                <th className={TH}>{t('cvm:party.sanctions.evidence')}</th>
              </tr>
            </thead>
            <tbody>
              {data.sanctions.map((s, index) => (
                <tr key={`${s.case_key}-${s.item_seq}-${index}`} className="border-t align-top">
                  <td className={TD}>
                    <Link
                      to={caseHref(s.case_key)}
                      className="text-primary underline-offset-2 hover:underline"
                    >
                      {s.process_number}
                    </Link>
                    <div className="text-xs text-muted-foreground">
                      {s.judgment_date ?? '—'} · {t('cvm:case.walk.item', { seq: s.item_seq })}
                    </div>
                  </td>
                  <td className={TD}>
                    {t(`cvm:sanctionTypes.${s.sanction_type}`, {
                      defaultValue: s.sanction_type,
                    })}
                    {s.duration_raw ? (
                      <div className="text-xs text-muted-foreground">{s.duration_raw}</div>
                    ) : null}
                  </td>
                  <td className={TD_NUM}>
                    {money(s.amount_brl)}
                    {s.amount_attribution ? (
                      <div className="text-xs font-normal text-muted-foreground">
                        {t(`cvm:attributions.${s.amount_attribution}`, {
                          defaultValue: s.amount_attribution,
                        })}
                      </div>
                    ) : null}
                  </td>
                  <td className={TD_MUTED}>{s.provision ?? '—'}</td>
                  <td className={TD_MUTED} title={s.evidence_span}>
                    {s.evidence_span.length > 140
                      ? `${s.evidence_span.slice(0, 140)}…`
                      : s.evidence_span}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <div className="grid gap-4 lg:grid-cols-2">
        <section className="rounded-lg border bg-card p-4" data-settlements>
          <h2 className="mb-2 text-sm font-semibold text-foreground">
            {t('cvm:party.settlements.title')}
          </h2>
          {data.settlements.length === 0 ? (
            <p className="text-xs text-muted-foreground">{t('cvm:party.none')}</p>
          ) : (
            <ul className="space-y-2 text-sm">
              {data.settlements.map((s, index) => (
                <li key={index} className="rounded-md border bg-muted/30 p-2 text-xs">
                  <div className="flex flex-wrap justify-between gap-2">
                    <span className="text-foreground">
                      {s.case_key ? (
                        <Link to={caseHref(s.case_key)} className="text-primary hover:underline">
                          {s.process_key ?? s.case_key}
                        </Link>
                      ) : (
                        (s.process_key ?? '—')
                      )}
                    </span>
                    <span className="text-muted-foreground">{s.decided_at ?? '—'}</span>
                  </div>
                  <div className="text-muted-foreground">
                    {t(`cvm:settlementOutcomes.${s.outcome}`, {
                      defaultValue: s.outcome,
                    })}{' '}
                    · {money(s.amount_brl)}
                    {s.is_unanimous == null
                      ? ''
                      : ` · ${s.is_unanimous ? t('cvm:case.settlements.unanimous') : t('cvm:case.settlements.majority')}`}
                    {' · '}
                    <a
                      href={s.decision_url}
                      target="_blank"
                      rel="noreferrer"
                      className="text-primary hover:underline"
                    >
                      {t('cvm:case.settlements.decision')}
                    </a>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="rounded-lg border bg-card p-4" data-appeals>
          <h2 className="mb-2 text-sm font-semibold text-foreground">
            {t('cvm:party.appeals.title')}
          </h2>
          {data.appeals.length === 0 ? (
            <p className="text-xs text-muted-foreground">{t('cvm:party.none')}</p>
          ) : (
            <ul className="space-y-2 text-sm">
              {data.appeals.map((a, index) => (
                <li key={index} className="rounded-md border bg-muted/30 p-2 text-xs">
                  <div className="flex flex-wrap justify-between gap-2">
                    <span className="text-foreground">
                      {a.case_key ? (
                        <Link to={caseHref(a.case_key)} className="text-primary hover:underline">
                          {a.process_key ?? a.case_key}
                        </Link>
                      ) : (
                        (a.process_key ?? '—')
                      )}
                    </span>
                    <span className="text-muted-foreground">
                      {a.session_number
                        ? t('cvm:case.appeals.session', {
                            number: a.session_number,
                          })
                        : '—'}
                    </span>
                  </div>
                  <div className="text-muted-foreground">
                    {t(`cvm:appealRoles.${a.role}`, { defaultValue: a.role })}
                    {' · '}
                    {a.outcome
                      ? t(`cvm:appealOutcomes.${a.outcome}`, {
                          defaultValue: a.outcome,
                        })
                      : t('cvm:case.appeals.unread')}
                    {a.sanction_after
                      ? ` · ${t(`cvm:sanctionAfter.${a.sanction_after}`, { defaultValue: a.sanction_after })}`
                      : ''}
                    {a.fine_after_appeal_brl ? ` · ${money(a.fine_after_appeal_brl)}` : ''}
                    {a.relator ? ` · ${a.relator}` : ''}
                    {' · '}
                    <a
                      href={a.page_url}
                      target="_blank"
                      rel="noreferrer"
                      className="text-primary hover:underline"
                    >
                      {t('cvm:case.appeals.page')}
                    </a>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      <section className="rounded-lg border bg-card p-4" data-events>
        <h2 className="mb-2 text-sm font-semibold text-foreground">
          {t('cvm:party.events.title')}
        </h2>
        {data.events.length === 0 ? (
          <p className="text-xs text-muted-foreground">{t('cvm:party.none')}</p>
        ) : (
          <ul className="space-y-1 text-xs">
            {data.events.map((e, index) => (
              <li key={index} className="flex flex-wrap justify-between gap-2 border-t pt-1">
                <span className="text-foreground">{e.nup_display ?? '—'}</span>
                <span className="text-muted-foreground">
                  {e.situacao ?? '—'} · {e.situacao_date ?? '—'}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
